import { useState, useEffect, useRef } from "react";
import { useAuth } from "../../context/AuthContext";
import { api } from "../../lib/api";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription, SheetFooter } from "../ui/sheet";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Badge } from "../ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { MultiSelectCombobox } from "../shared/MultiSelectCombobox";
import { toast } from "sonner";
import { ExternalLink, Loader2, GitBranch, Paperclip, Download, Trash2, FileDown, Send, MessageSquare, X } from "lucide-react";

// Ficha completa en drawer lateral — feedback Ecosistema/Partners 2026-08-31 P0
// punto 3: "no se entra a la ficha del lead Airtable, faltan campos". Muestra y
// edita todos los campos disponibles (curated y/o airtable_ecosystem fusionados).
// Delta tarde 2026-08-31: + Tagline, Adjuntos (Object Storage), PDF de ficha,
// Comentarios con @menciones, "Pasar a Dealflow" simplificado (sin elegir etapa
// aquí — se asigna luego en Pipeline B2B > "sin etapa asignada").
export function PartnerDrawer({ open, agent, catalogs, onClose, onSaved }) {
  const { token, user } = useAuth();
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [addingToDealflow, setAddingToDealflow] = useState(false);
  const [attachments, setAttachments] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  const [comments, setComments] = useState([]);
  const [loadingComments, setLoadingComments] = useState(false);
  const [commentText, setCommentText] = useState("");
  const [sendingComment, setSendingComment] = useState(false);
  const [teamUsers, setTeamUsers] = useState([]);
  const [mentionMatches, setMentionMatches] = useState([]);
  const [mentionRange, setMentionRange] = useState(null); // {start, end} en commentText
  const fileInputRef = useRef(null);
  const commentInputRef = useRef(null);

  useEffect(() => {
    if (open) {
      // Fix 2026-08-31 ("Auto-completar Mención"): lista de usuarios para el
      // desplegable al escribir @ en un comentario — se carga una vez al abrir.
      api.getUsers(token).then(setTeamUsers).catch(() => setTeamUsers([]));
    }
  }, [open, token]);

  useEffect(() => {
    if (agent) {
      setForm({
        tagline: agent.tagline || "",
        description: agent.description || "", country: agent.country || "", city: agent.city || "",
        website: agent.website || "", specialty: agent.specialty || [], founded: agent.founded || "",
        employees: agent.employees || "", fit_negocio: agent.fit_negocio || [], origin: agent.origin || [],
        event_leads: agent.event_leads || [], batch: agent.batch || [], pipeline: agent.pipeline || "",
        status: agent.status || [], contacts: agent.contacts || "", info_adicional: agent.info_adicional || "",
      });
      setAttachments(agent.attachments || []);
      setAddingToDealflow(false);
      setCommentText("");
      setMentionMatches([]);
      setMentionRange(null);
      setLoadingComments(true);
      api.getPartnerComments(token, { curatedId: agent.curated_id, ecosystemId: agent.ecosystem_id })
        .then(setComments)
        .catch(() => setComments([]))
        .finally(() => setLoadingComments(false));
    }
  }, [agent, token]);

  // Fix 2026-08-31 ("Auto-completar Mención"): detecta el "@" activo justo
  // antes del cursor y filtra el equipo por nombre, en vez de tener que
  // escribir el nombre completo a mano.
  const handleCommentChange = (e) => {
    const value = e.target.value;
    const cursor = e.target.selectionStart;
    setCommentText(value);
    const uptoCursor = value.slice(0, cursor);
    const at = uptoCursor.lastIndexOf("@");
    if (at === -1) { setMentionMatches([]); setMentionRange(null); return; }
    const query = uptoCursor.slice(at + 1);
    if (query.includes("\n") || query.length > 30) { setMentionMatches([]); setMentionRange(null); return; }
    const matches = teamUsers.filter((u) => u.full_name?.toLowerCase().includes(query.toLowerCase())).slice(0, 6);
    setMentionMatches(query.length >= 0 ? matches : []);
    setMentionRange({ start: at, end: cursor });
  };

  const handleSelectMention = (user) => {
    if (!mentionRange) return;
    const before = commentText.slice(0, mentionRange.start);
    const after = commentText.slice(mentionRange.end);
    const next = `${before}@${user.full_name} ${after}`;
    setCommentText(next);
    setMentionMatches([]);
    setMentionRange(null);
    commentInputRef.current?.focus();
  };

  if (!agent || !form) return null;
  const set = (field, val) => setForm((f) => ({ ...f, [field]: val }));

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.savePartnerUnified(token, { curatedId: agent.curated_id, ecosystemId: agent.ecosystem_id, fields: form });
      toast.success("Ficha guardada");
      onSaved?.();
    } catch {
      toast.error("Error al guardar la ficha");
    } finally {
      setSaving(false);
    }
  };

  const handleAddToDealflow = async () => {
    setAddingToDealflow(true);
    try {
      await api.savePartnerUnified(token, { curatedId: agent.curated_id, ecosystemId: agent.ecosystem_id, fields: { dealflow: true } });
      toast.success("Añadido al Dealflow — asígnale una etapa en Ecosistema > Dealflow > Pipeline B2B");
      onSaved?.();
    } catch {
      toast.error("Error al añadir al dealflow");
    } finally {
      setAddingToDealflow(false);
    }
  };

  const handleUploadFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!agent.curated_id) {
      toast.error("Guarda primero el agente en el Directorio (Nuevo Agente) para poder adjuntar archivos");
      return;
    }
    setUploading(true);
    try {
      const doc = await api.uploadPartnerAttachment(token, agent.curated_id, file);
      setAttachments((prev) => [...prev, doc]);
      toast.success("Adjunto subido");
    } catch {
      toast.error("Error al subir el adjunto");
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const handleDownloadAttachment = async (docId, filename) => {
    try {
      const response = await api.downloadPartnerAttachment(token, agent.curated_id, docId);
      const blobUrl = window.URL.createObjectURL(new Blob([response.data], { type: response.headers?.["content-type"] }));
      const link = document.createElement("a");
      link.href = blobUrl;
      link.setAttribute("download", filename || "adjunto");
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(blobUrl);
    } catch {
      toast.error("Error al descargar el adjunto");
    }
  };

  const handleDeleteAttachment = async (docId) => {
    try {
      await api.deletePartnerAttachment(token, agent.curated_id, docId);
      setAttachments((prev) => prev.filter((a) => a.id !== docId));
      toast.success("Adjunto eliminado");
    } catch {
      toast.error("Error al eliminar el adjunto");
    }
  };

  const handleDownloadPdf = async () => {
    setDownloadingPdf(true);
    try {
      const data = await api.downloadPartnerPdf(token, { curatedId: agent.curated_id, ecosystemId: agent.ecosystem_id });
      const blobUrl = window.URL.createObjectURL(new Blob([data], { type: "application/pdf" }));
      const link = document.createElement("a");
      link.href = blobUrl;
      link.setAttribute("download", `Ficha_${(agent.name || "agente").replace(/[^a-zA-Z0-9]+/g, "_")}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(blobUrl);
    } catch {
      toast.error("Error al generar el PDF");
    } finally {
      setDownloadingPdf(false);
    }
  };

  const handleSendComment = async () => {
    if (!commentText.trim()) return;
    setSendingComment(true);
    try {
      const comment = await api.createPartnerComment(token, {
        curatedId: agent.curated_id, ecosystemId: agent.ecosystem_id, text: commentText.trim(), agentName: agent.name,
      });
      setComments((prev) => [...prev, comment]);
      setCommentText("");
      setMentionMatches([]);
      setMentionRange(null);
    } catch {
      toast.error("Error al publicar el comentario");
    } finally {
      setSendingComment(false);
    }
  };

  return (
    <Sheet open={open} onOpenChange={(v) => !v && onClose()}>
      <SheetContent className="w-full sm:max-w-xl overflow-y-auto" data-testid="partner-drawer">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2 flex-wrap">
            {agent.name}
            {agent.sources?.includes("curated") && <Badge>Curado</Badge>}
            {agent.sources?.includes("airtable_ecosystem") && <Badge variant="secondary">Ecosistema (Airtable)</Badge>}
          </SheetTitle>
          <SheetDescription>Ficha completa del agente — editable, no se escribe en Airtable.</SheetDescription>
        </SheetHeader>

        {/* Fix "resto Ecosistema" 2026-08-31 punto 4 ("Autoría en la ficha") */}
        {(agent.created_by || agent.updated_by) && (
          <p className="text-[10px] text-muted-foreground -mt-2" data-testid="drawer-autoria">
            {agent.created_by && <>Creado por {agent.created_by}</>}
            {agent.created_by && agent.updated_by && " · "}
            {agent.updated_by && (
              <>Última modificación: {agent.updated_by}{agent.updated_at && ` el ${new Date(agent.updated_at).toLocaleString("es-ES")}`}</>
            )}
          </p>
        )}

        <div className="flex justify-end -mt-1">
          <Button type="button" variant="outline" size="sm" onClick={handleDownloadPdf} disabled={downloadingPdf} className="gap-1.5" data-testid="drawer-download-pdf-btn">
            {downloadingPdf ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <FileDown className="w-3.5 h-3.5" />}
            Descargar PDF
          </Button>
        </div>

        <div className="space-y-4 mt-2 pb-4">
          <div className="space-y-2">
            <Label>Tagline</Label>
            <Input placeholder="Frase corta que resume al agente" value={form.tagline} onChange={(e) => set("tagline", e.target.value)} data-testid="drawer-tagline" />
          </div>
          <div className="space-y-2">
            <Label>Descripción</Label>
            <Textarea rows={2} value={form.description} onChange={(e) => set("description", e.target.value)} data-testid="drawer-description" />
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div className="space-y-2"><Label>País</Label><Input value={form.country} onChange={(e) => set("country", e.target.value)} data-testid="drawer-country" /></div>
            <div className="space-y-2"><Label>Ciudad</Label><Input value={form.city} onChange={(e) => set("city", e.target.value)} data-testid="drawer-city" /></div>
            <div className="space-y-2">
              <Label>Web</Label>
              <div className="flex gap-1">
                <Input value={form.website} onChange={(e) => set("website", e.target.value)} data-testid="drawer-website" />
                {form.website && <a href={form.website} target="_blank" rel="noreferrer"><Button type="button" size="icon" variant="outline"><ExternalLink className="w-4 h-4" /></Button></a>}
              </div>
            </div>
          </div>
          <div className="space-y-2">
            <Label>Especialidad</Label>
            <MultiSelectCombobox options={catalogs?.specialty || []} value={form.specialty} onChange={(v) => set("specialty", v)} testId="drawer-specialty" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2"><Label>Fundación</Label><Input value={form.founded} onChange={(e) => set("founded", e.target.value)} data-testid="drawer-founded" /></div>
            <div className="space-y-2">
              <Label>Empleados</Label>
              <Select value={form.employees} onValueChange={(v) => set("employees", v)}>
                <SelectTrigger data-testid="drawer-employees"><SelectValue placeholder="—" /></SelectTrigger>
                <SelectContent>{(catalogs?.employees || []).map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <div className="space-y-2">
            <Label>Fit negocio (UN / negocio)</Label>
            <MultiSelectCombobox options={catalogs?.fit_negocio || []} value={form.fit_negocio} onChange={(v) => set("fit_negocio", v)} testId="drawer-fit-negocio" />
          </div>
          <div className="space-y-2">
            <Label>Origen</Label>
            <MultiSelectCombobox options={catalogs?.origin || []} value={form.origin} onChange={(v) => set("origin", v)} testId="drawer-origin" />
          </div>
          <div className="space-y-2">
            <Label>Event Leads</Label>
            <MultiSelectCombobox options={catalogs?.event_leads || []} value={form.event_leads} onChange={(v) => set("event_leads", v)} testId="drawer-event-leads" />
          </div>
          <div className="space-y-2">
            <Label>Colección / Batch</Label>
            <MultiSelectCombobox options={catalogs?.batch || []} value={form.batch} onChange={(v) => set("batch", v)} testId="drawer-batch" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label>Pipeline</Label>
              <Select value={form.pipeline} onValueChange={(v) => set("pipeline", v)}>
                <SelectTrigger data-testid="drawer-pipeline"><SelectValue placeholder="—" /></SelectTrigger>
                <SelectContent>{(catalogs?.pipeline || []).map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Status</Label>
              <MultiSelectCombobox options={catalogs?.status || []} value={form.status} onChange={(v) => set("status", v)} testId="drawer-status" />
            </div>
          </div>
          <div className="space-y-2"><Label>Contactos</Label><Input value={form.contacts} onChange={(e) => set("contacts", e.target.value)} data-testid="drawer-contacts" /></div>
          <div className="space-y-2"><Label>Info adicional</Label><Textarea rows={2} value={form.info_adicional} onChange={(e) => set("info_adicional", e.target.value)} data-testid="drawer-info-adicional" /></div>

          {/* Adjuntos — Emergent Object Storage */}
          <div className="rounded-lg border p-3 space-y-2 bg-muted/30" data-testid="drawer-attachments-section">
            <div className="text-sm font-medium flex items-center gap-2"><Paperclip className="w-4 h-4" />Adjuntos</div>
            {attachments.length > 0 && (
              <ul className="space-y-1">
                {attachments.map((a) => (
                  <li key={a.id} className="flex items-center justify-between gap-2 text-xs bg-background rounded-md border px-2 py-1.5" data-testid={`drawer-attachment-${a.id}`}>
                    <span className="truncate flex-1">{a.original_filename}</span>
                    <button type="button" onClick={() => handleDownloadAttachment(a.id, a.original_filename)} className="text-primary hover:text-primary/70" data-testid={`drawer-attachment-download-${a.id}`}>
                      <Download className="w-3.5 h-3.5" />
                    </button>
                    <button type="button" onClick={() => handleDeleteAttachment(a.id)} className="text-destructive hover:text-destructive/70" data-testid={`drawer-attachment-delete-${a.id}`}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </li>
                ))}
              </ul>
            )}
            {agent.curated_id ? (
              <div className="flex items-center gap-2">
                <Input ref={fileInputRef} type="file" onChange={handleUploadFile} disabled={uploading} className="text-xs" data-testid="drawer-attachment-upload-input" />
                {uploading && <Loader2 className="w-3.5 h-3.5 animate-spin flex-shrink-0" />}
              </div>
            ) : (
              <p className="text-xs text-muted-foreground italic">Este agente solo existe en Airtable (Ecosistema) — guárdalo desde "+ Nuevo Agente" para poder adjuntar archivos.</p>
            )}
          </div>

          {/* Dealflow — flujo simplificado (delta tarde): un único paso, sin
              elegir etapa aquí; la etapa se asigna luego desde el aviso "sin
              etapa asignada" de Pipeline B2B. */}
          <div className="rounded-lg border p-3 space-y-2 bg-muted/30" data-testid="drawer-dealflow-section">
            <div className="text-sm font-medium flex items-center gap-2"><GitBranch className="w-4 h-4" />Dealflow</div>
            {agent.dealflow ? (
              <Badge variant="secondary" data-testid="drawer-dealflow-badge">Ya está en el Dealflow — revisa su etapa en Ecosistema &gt; Dealflow &gt; Pipeline B2B</Badge>
            ) : (
              <Button type="button" variant="outline" size="sm" onClick={handleAddToDealflow} disabled={addingToDealflow} className="gap-1.5" data-testid="drawer-add-dealflow-btn">
                {addingToDealflow && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
                Pasar a Dealflow
              </Button>
            )}
          </div>

          {/* Comentarios con @menciones */}
          <div className="rounded-lg border p-3 space-y-2 bg-muted/30" data-testid="drawer-comments-section">
            <div className="text-sm font-medium flex items-center gap-2"><MessageSquare className="w-4 h-4" />Comentarios{comments.length > 0 ? ` (${comments.length})` : ""}</div>
            {loadingComments ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <ul className="space-y-2 max-h-52 overflow-y-auto">
                {comments.map((c) => (
                  <li key={c.id} className="text-xs bg-background rounded-md border px-2.5 py-1.5" data-testid={`drawer-comment-${c.id}`}>
                    <div className="flex items-center justify-between gap-2">
                      <span className="font-semibold">{c.author_name}</span>
                      <span className="text-[10px] text-muted-foreground">{new Date(c.created_at).toLocaleString("es-ES")}</span>
                    </div>
                    <p className="mt-0.5 whitespace-pre-wrap">{c.text}</p>
                    <CommentReactions comment={c} token={token} userEmail={user?.email} onChange={(reactions) => setComments((prev) => prev.map((x) => x.id === c.id ? { ...x, reactions } : x))} />
                  </li>
                ))}
                {comments.length === 0 && <li className="text-xs text-muted-foreground italic">Sin comentarios todavía.</li>}
              </ul>
            )}
            <div className="flex gap-2 items-start relative">
              <div className="flex-1 relative">
                <Textarea
                  ref={commentInputRef}
                  rows={2} placeholder="Escribe un comentario… usa @Nombre para mencionar a alguien del equipo"
                  value={commentText} onChange={handleCommentChange}
                  className="text-xs" data-testid="drawer-comment-input"
                />
                {mentionMatches.length > 0 && (
                  <ul className="absolute z-10 bottom-full mb-1 left-0 w-56 bg-popover border rounded-md shadow-lg overflow-hidden" data-testid="drawer-mention-suggestions">
                    {mentionMatches.map((u) => (
                      <li key={u.id}>
                        <button
                          type="button"
                          onClick={() => handleSelectMention(u)}
                          className="w-full text-left text-xs px-2.5 py-1.5 hover:bg-muted flex flex-col"
                          data-testid={`drawer-mention-option-${u.id}`}
                        >
                          <span className="font-medium">{u.full_name}</span>
                          <span className="text-[10px] text-muted-foreground">{u.email}</span>
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
              <Button type="button" size="icon" onClick={handleSendComment} disabled={sendingComment || !commentText.trim()} data-testid="drawer-comment-send-btn">
                {sendingComment ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              </Button>
            </div>
          </div>
        </div>

        <SheetFooter>
          <Button variant="outline" onClick={onClose} data-testid="drawer-close-btn"><X className="w-4 h-4 mr-1" />Cerrar</Button>
          <Button onClick={handleSave} disabled={saving} data-testid="drawer-save-btn">
            {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            Guardar
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}

const QUICK_REACTION_EMOJIS = ["👍", "👀", "✅", "🎉"];
const REACTION_SLUGS = { "👍": "like", "👀": "seen", "✅": "done", "🎉": "celebrate" };

// Fix 2026-08-31 ("Reacciones a Comentarios"): marcar un comentario como
// "visto" (👀) o con un emoji rápido — toggle, agrupado por emoji con contador.
function CommentReactions({ comment, token, userEmail, onChange }) {
  const [busy, setBusy] = useState(false);
  const reactions = comment.reactions || [];
  const grouped = QUICK_REACTION_EMOJIS.map((emoji) => ({
    emoji,
    users: reactions.filter((r) => r.emoji === emoji),
    mine: reactions.some((r) => r.emoji === emoji && r.user_email === userEmail),
  }));

  const toggle = async (emoji) => {
    setBusy(true);
    try {
      const res = await api.reactToPartnerComment(token, comment.id, emoji);
      onChange(res.reactions);
    } catch {
      toast.error("Error al reaccionar");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex items-center gap-1 mt-1" data-testid={`drawer-comment-reactions-${comment.id}`}>
      {grouped.map((g) => (
        <button
          key={g.emoji}
          type="button"
          disabled={busy}
          onClick={() => toggle(g.emoji)}
          title={g.users.map((u) => u.user_name).join(", ") || "Reaccionar"}
          className={`text-[10px] px-1.5 py-0.5 rounded-full border flex items-center gap-1 transition-colors ${
            g.mine ? "bg-emerald-100 dark:bg-emerald-900/40 border-emerald-300 text-emerald-700 dark:text-emerald-300" : "bg-muted/50 border-border text-muted-foreground hover:bg-muted"
          }`}
          data-testid={`drawer-comment-react-${comment.id}-${REACTION_SLUGS[g.emoji]}`}
        >
          {g.emoji}{g.users.length > 0 && <span>{g.users.length}</span>}
        </button>
      ))}
    </div>
  );
}
